import { useGetMachineAgentId, useRoomAgent } from '#~/agent/components/bridge/hooks.js';
import { DeviceImage } from '#~/agent/components/bridge/screens/device-img.js';
import { LinkBridgeCopy } from '#~/agent/components/bridge/screens/welcome.js';
import { Name } from '#~/agent/components/monitoring/agent/name.js';
import { useAppVersion } from '#~/agent/hooks.js';
import { ElectronAPI } from '#~/electron/index.js';
import { desktopUpdateIdleLabel } from '#~/electron/update-check-status.js';
import { PostHogFeatureFlag, useFeatureFlag } from '#~/posthog/index.js';
import { Button } from '@studio/design-system/button';
import { getCurrentVendorBadgeIcon, vendorName } from '@studio/design-system/themes/vendor.js';
import { isNotNil } from '@tryiris-ai/js-types';
import { clsx } from 'cn';
import prettyBytes from 'pretty-bytes';

export const Linked = () => {
  const { agentId: machineAgentId } = useGetMachineAgentId();
  const roomAgent = useRoomAgent(machineAgentId);

  const { enabled: isBridgeEnabled } = useFeatureFlag(PostHogFeatureFlag.BRIDGE_FEATURE);

  if (!roomAgent) {
    return <div>Room agent not found</div>;
  }

  const { agentStats, updateStatus, update, unlink, agent, wiring, offline } = roomAgent;

  return (
    <>
      <div className='p-16 pb-24 text-center'>
        <DeviceImage />
        <div className='mt-8 flex justify-center'>
          <div
            className={clsx(
              'rounded-8 px-12 py-6',
              'flex items-center gap-4 text-sm font-medium',
              'transition-colors duration-750',
              'bg-base-300'
            )}
          >
            <span
              className={clsx('i-mdi-circle text-[6px]', {
                'text-error': offline,
                'text-success': !offline
              })}
            />
            {agent.status == 'online' ? 'Online' : 'Offline'}
          </div>
        </div>
        <div>
          <div className='mt-12 flex justify-center'>
            <Name agentId={agent.id} wiring={wiring} />
          </div>
        </div>
        <div className='mt-20 flex justify-center gap-12'>
          <Button
            size='lg'
            className='flex items-center gap-4'
            onClick={() => {
              ElectronAPI.restart();
            }}
          >
            <span>Restart</span>
          </Button>
          {isBridgeEnabled && (
            <Button variant='secondary' size='lg' onClick={unlink}>
              Unlink
            </Button>
          )}
        </div>
        <div className='mt-24 rounded-4 bg-base-200'>
          <div className='flex text-left'>
            <div className='flex flex-1 flex-col gap-8 border-r-1 border-base-100 p-16'>
              <div className='text-sm text-muted'>CPU Usage</div>
              <div>{isNotNil(agentStats?.cpuUsage) ? `${agentStats.cpuUsage}%` : '—'}</div>
            </div>
            <div className='flex flex-1 flex-col gap-8 border-r-1 border-base-100 p-16'>
              <div className='text-sm text-muted'>RAM Usage</div>
              <div>{isNotNil(agentStats?.ramUsage) ? `${agentStats.ramUsage}%` : '—'}</div>
            </div>
            <div className='flex flex-1 flex-col gap-8 p-16'>
              <div className='text-sm text-muted'>Disk Space</div>
              <div>
                {isNotNil(agentStats?.diskSpace)
                  ? `${prettyBytes(agentStats.diskSpace, { binary: false })}`
                  : '—'}
              </div>
            </div>
          </div>
          <div className='flex items-center justify-between border-t-1 border-base-100 px-16 py-10 text-sm text-muted'>
            <div>
              {vendorName()} version: {agentStats?.version}
            </div>
            {desktopUpdateIdleLabel(updateStatus ?? {}) === 'check-failed' && (
              <div className='text-muted'>Couldn't check for updates</div>
            )}
            {desktopUpdateIdleLabel(updateStatus ?? {}) === 'up-to-date' && (
              <div className='text-muted'>Up To Date</div>
            )}

            {updateStatus?.updateAvailable &&
              !updateStatus.isDownloading &&
              !updateStatus.isReadyToInstall && (
                <Button variant='link' onClick={update}>
                  Update
                </Button>
              )}

            {updateStatus?.updateAvailable && updateStatus.isDownloading && (
              <Button variant='link' disabled>
                Downloading...
              </Button>
            )}

            {updateStatus?.updateAvailable && updateStatus.isReadyToInstall && (
              <Button variant='link' onClick={update}>
                Restart & Install
              </Button>
            )}

            {updateStatus?.isInstalling && (
              <Button variant='link' disabled>
                Installing...
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export const LoggedOut = ({ onSignin }: { onSignin: () => void }) => {
  const appVersion = useAppVersion();

  return (
    <div className='py-24'>
      <img src={getCurrentVendorBadgeIcon()} className='mx-auto mt-8 h-[80px]' />
      <div className='mt-16 flex flex-col gap-8 text-center'>
        <div className='text-md font-demibold text-primary'>
          {vendorName()} version: {appVersion}
        </div>
      </div>
      <div className='my-24 border-b-1 border-base-50' />
      <div className='px-16 text-center'>
        <div className='flex flex-col gap-8'>
          <h1 className='my-0 text-lg'>Sign In</h1>
          <p className='text-sm text-muted'>Sign in to access your studio.</p>
        </div>
      </div>
      <Button size='lg' className='mx-auto mt-16' onClick={onSignin}>
        Sign In
      </Button>
    </div>
  );
};

export const Unlinked = ({ onLink }: { onLink: () => void }) => {
  const appVersion = useAppVersion();

  return (
    <div className='py-24'>
      <img src={getCurrentVendorBadgeIcon()} className='mx-auto mt-8 h-[80px]' />
      <div className='mt-16 flex flex-col gap-8 text-center'>
        <div className='text-md font-demibold text-primary'>
          {vendorName()} Version: {appVersion}
        </div>
      </div>
      <div className='my-24 border-b-1 border-base-50' />
      <div className='px-16 text-center'>
        <LinkBridgeCopy />
      </div>
      <Button size='lg' className='mx-auto mt-16' onClick={onLink}>
        Link Bridge
      </Button>
    </div>
  );
};
